import { useEffect, useMemo, useState } from "react";
import { t } from "ttag";

import { useStorageSetup } from "metabase/common/components/upsells/StoragePurchaseModal";
import { useSetting } from "metabase/common/hooks";
import CS from "metabase/css/core/index.css";
import { PLUGIN_UPLOAD_MANAGEMENT } from "metabase/plugins";
import { Box, Icon, Modal, Tabs } from "metabase/ui";
import type { IconName } from "metabase-types/api";

import S from "./AddDataModal.module.css";
import { CSVPanel } from "./Panels/CSVPanel";
import { DatabasesPanel } from "./Panels/DatabasesPanel";
import { PanelsHeader } from "./Panels/PanelsHeader";
import { trackAddDataEvent } from "./analytics";
import { useAddDataPermissions } from "./use-add-data-permission";
import { type AddDataTab, isValidTab } from "./utils";

interface AddDataModalProps {
  opened: boolean;
  onClose: () => void;

  initialTab?: AddDataTab;
  fromEmbeddingSetupGuide?: boolean;
}

interface Tabs {
  name: string;
  value: "csv" | "db" | "gsheets";
  isVisible: boolean;
  iconName: IconName;
}

export const AddDataModal = (props: AddDataModalProps) => (
  // The provider sits outside `Modal.Root` so the storage setup state and its
  // polling survive the modal being closed and reopened, and so the purchase
  // confirmation modal it hosts can replace this modal instead of stacking.
  // `enabled` defers the add-ons fetch until the modal is actually shown.
  <PLUGIN_UPLOAD_MANAGEMENT.StorageSetupProvider enabled={props.opened}>
    <AddDataModalContent {...props} />
  </PLUGIN_UPLOAD_MANAGEMENT.StorageSetupProvider>
);

const AddDataModalContent = ({
  opened,
  onClose,
  initialTab,
  fromEmbeddingSetupGuide,
}: AddDataModalProps) => {
  const {
    areUploadsEnabled,
    canUploadToDatabase,
    canManageUploads,
    isAdmin,
    canPerformMeaningfulActions,
    hasUploadableDatabases,
  } = useAddDataPermissions();
  const { isPurchaseModalOpened, canSetUpStorage } = useStorageSetup();

  const shouldShowUploadsTab =
    hasUploadableDatabases || canPerformMeaningfulActions || canSetUpStorage;

  const [activeTab, setActiveTab] = useState<Tabs["value"] | null>(null);
  const isHosted = useSetting("is-hosted?");

  const handleTabChange = (tabValue: string | null) => {
    if (tabValue === activeTab || !isValidTab(tabValue)) {
      return;
    }

    const eventMapping = {
      db: "database_tab_clicked",
      csv: "csv_tab_clicked",
      gsheets: "sheets_tab_clicked",
    } as const satisfies Record<AddDataTab, string>;

    trackAddDataEvent(eventMapping[tabValue]);
    setActiveTab(tabValue);
  };

  const tabs = useMemo(() => {
    return [
      {
        name: t`Database`,
        value: "db",
        isVisible: true,
        iconName: "database",
      },
      {
        name: t`CSV`,
        value: "csv",
        isVisible: shouldShowUploadsTab,
        iconName: "table2",
      },
      {
        name: t`Google Sheets`,
        value: "gsheets",
        isVisible: isHosted,
        iconName: "document",
      },
    ] satisfies Tabs[];
  }, [shouldShowUploadsTab, isHosted]);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    } else {
      const firstVisibleTab = tabs.find((tab) => tab.isVisible);

      if (firstVisibleTab) {
        setActiveTab(firstVisibleTab.value);
      }
    }
  }, [tabs, initialTab]);

  return (
    // The modal hides itself (stays mounted) while the storage purchase
    // confirmation is shown, so the confirmation replaces it instead of
    // stacking on top of it.
    <Modal.Root
      opened={opened && !isPurchaseModalOpened}
      onClose={onClose}
      size="auto"
    >
      <Modal.Overlay />
      <Modal.Content h="30rem">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="none"
          orientation="vertical"
          classNames={{
            list: S.list,
            tab: S.tab,
            tabLabel: S.tabLabel,
          }}
          h="100%"
        >
          <Box component="nav" w="14rem" className={CS.borderRight}>
            <Box component="header" className={S.header}>
              <Modal.Title fz="lg">{t`Add data`}</Modal.Title>
            </Box>
            <Tabs.List px="md" pb="lg">
              {tabs
                .filter((tab) => tab.isVisible)
                .map((tab) => (
                  <Tabs.Tab
                    key={tab.value}
                    value={tab.value}
                    leftSection={<Icon name={tab.iconName} />}
                  >
                    {tab.name}
                  </Tabs.Tab>
                ))}
            </Tabs.List>
          </Box>
          <Box component="main" w="30rem" className={S.panelContainer}>
            <PanelsHeader
              showDatabasesLink={activeTab === "db" && isAdmin}
              showUploadsLink={
                activeTab === "csv" && canManageUploads && areUploadsEnabled
              }
              showManageImports={
                activeTab === "gsheets" && isAdmin && areUploadsEnabled
              }
              onAddDataModalClose={onClose}
            />
            <Tabs.Panel value="db" className={S.panel}>
              <DatabasesPanel
                canSeeContent={isAdmin}
                fromEmbeddingSetupGuide={fromEmbeddingSetupGuide}
              />
            </Tabs.Panel>
            <Tabs.Panel value="csv" className={S.panel}>
              <CSVPanel
                onCloseAddDataModal={onClose}
                uploadsEnabled={areUploadsEnabled}
                canUpload={canUploadToDatabase}
                canManageUploads={canManageUploads}
              />
            </Tabs.Panel>
            <Tabs.Panel value="gsheets" className={S.panel}>
              <PLUGIN_UPLOAD_MANAGEMENT.GdriveAddDataPanel
                onAddDataModalClose={onClose}
              />
            </Tabs.Panel>
          </Box>
        </Tabs>
      </Modal.Content>
    </Modal.Root>
  );
};
